import { Formik } from "formik";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { CompletionUserInput } from "@baiki/interfaces";

import { getVoiceConfigForLanguage } from "@/config";
import { FeaturesHooks } from "@/hooks";
import { useAuth } from "@/providers";
import { JobsQueries } from "@/queries";
import { Storage } from "@/store";

import { ComposeDialog } from "./ComposeDialog";

interface ComposeFlowProps {
  onExit: () => void;
  open: boolean;
}

const ComposeFlow: React.FC<ComposeFlowProps> = ({ open, onExit }) => {
  const { t, i18n } = useTranslation();
  const [settings] = useAtom(Storage.settings);
  const createJob = JobsQueries.useCreateJob();
  const { user } = useAuth();
  const { canCompose } = FeaturesHooks.useFeatureFlags();
  // One job at a time. Last line of defence: the dialog can already be open
  // when a job starts elsewhere (another device, or the story page's translate
  // menu), so the gate is re-checked at submit, not just at the entry points.
  const hasActiveJob = JobsQueries.useHasActiveJob();

  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [jobId, setJobId] = useState<string>();

  const validationSchema = object().shape({
    what: string()
      .min(10, t("fields.what.validation.minLength"))
      .required(t("fields.what.validation.required")),
    who: string()
      .min(3, t("fields.who.validation.minLength"))
      .required(t("fields.who.validation.required")),
  });

  const handleSubmit = useCallback(
    async (values: CompletionUserInput) => {
      if (!user || !canCompose || hasActiveJob) return;

      try {
        const language = settings.language || i18n.language;
        if (!language) return;

        const newJobId = await createJob.mutateAsync({
          authorId: user.uid,
          language,
          voiceConfig: getVoiceConfigForLanguage(language),
          what: values.what,
          who: values.who,
        });

        console.log(
          `[NEW STORY] Job created with ID: ${newJobId}, language: ${language}`,
        );

        setJobId(newJobId);
      } catch (error) {
        console.error("Error creating job:", error);
      }
    },
    [
      user,
      settings.language,
      i18n.language,
      createJob,
      canCompose,
      hasActiveJob,
    ],
  );

  const handleClose = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
    setJobId(undefined);
    onExit();
  }, [onExit]);

  useEffect(() => {
    if (jobId) {
      autoCloseTimerRef.current = setTimeout(() => {
        handleClose();
      }, 10000);
      return () => {
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
        }
      };
    }
  }, [jobId, handleClose]);

  return (
    <Formik<CompletionUserInput>
      initialValues={{ what: "", who: "" }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ resetForm }) => (
        <ComposeDialog
          close={handleClose}
          isComposing={!!jobId}
          onClose={handleClose}
          open={open}
          slotProps={{
            transition: {
              onExited: () => {
                resetForm();
                setJobId(undefined);
              },
            },
          }}
        />
      )}
    </Formik>
  );
};

export const useComposeFlow = () => {
  const { user } = useAuth();

  const [open, setOpen] = useState<boolean>(false);

  const { canCompose } = FeaturesHooks.useFeatureFlags();

  const enterCompose = useCallback(() => {
    if (user && canCompose) setOpen(true);
  }, [canCompose, user]);

  const exitCompose = () => {
    setOpen(false);
  };

  const composeFlow = <ComposeFlow onExit={exitCompose} open={open} />;

  return { enterCompose, composeFlow };
};
